/**
 * Collect and preload scenario image assets by scene / chapter / mission.
 * Blocking load only waits for the entry scene; the rest is fire-and-forget prefetch.
 */

import type { Scenario, Scene } from '../types';
import { sceneIsChapterEnd } from './chapter-end-content';
import {
  resolveCharacterPortrait,
  resolveDialogueMood,
  resolveMoodImage,
} from './character-runtime';
import { buildChapterGroups, buildInlineStructure, type InlineChapterGroup } from './inline-scene-model';
import { isVideoAsset, preloadVideo } from './media-utils';
import { getMissionStartScene } from './mission-progress';
import { loadGame } from './save-load-manager';
import { getSceneById, resolveNextSceneId, resolveVisualState } from './scene-flow';
import { resolveStageFromSpine } from './stage-runtime';

export const LOOKAHEAD_SCENE_COUNT = 20;
export const PRELOAD_CONCURRENCY = 6;

const loadedUrls = new Set<string>();
const inFlight = new Map<string, Promise<void>>();

export function clearAssetPreloadCache(): void {
  loadedUrls.clear();
  inFlight.clear();
}

export function flattenSceneTree(scenes: Scene[]): Scene[] {
  const out: Scene[] = [];
  const walk = (list: Scene[]) => {
    for (const scene of list) {
      out.push(scene);
      if (scene.type === 'choice') {
        for (const option of scene.options ?? []) {
          if (option.scenes?.length) walk(option.scenes);
        }
      }
    }
  };
  walk(scenes);
  return out;
}

function addUrl(url: string | undefined, into: Set<string>): void {
  if (url) into.add(url);
}

function addCharacterAssets(
  scenario: Scenario,
  characterId: string | undefined,
  emotion: string | undefined,
  into: Set<string>,
): void {
  if (!characterId) return;
  const character = resolveCharacterPortrait(scenario.metadata.characters, characterId);
  if (!character) return;
  addUrl(character.icon, into);
  addUrl(resolveMoodImage(character, emotion ?? 'neutre'), into);
}

/** Assets referenced directly by one scene (no nested choice branches). */
export function collectDirectSceneAssetUrls(scene: Scene, scenario: Scenario): string[] {
  const urls = new Set<string>();
  addUrl(scene.background, urls);

  for (const element of scene.elements ?? []) {
    addUrl(element.image, urls);
    if (element.moods) {
      for (const moodUrl of Object.values(element.moods)) {
        addUrl(moodUrl, urls);
      }
    }
  }

  for (const stageCharacter of resolveStageFromSpine(scenario, scene.id)) {
    addCharacterAssets(scenario, stageCharacter.characterId, stageCharacter.emotion, urls);
  }

  if (scene.type === 'dialogue') {
    addCharacterAssets(scenario, scene.characterId, resolveDialogueMood(scene), urls);
  }
  if (scene.type === 'textOverlay') {
    addUrl(scene.characterIcon, urls);
  }
  if (scene.type === 'phoneCall' || scene.type === 'phoneChat') {
    addUrl(scene.contactAvatar, urls);
  }
  if (scene.type === 'dragDropGame') {
    addUrl(scene.speakerIcon, urls);
    addUrl(scene.backgroundImage, urls);
  }
  if (scene.type === 'situationSortGame') {
    addCharacterAssets(scenario, scene.speakerCharacterId, undefined, urls);
  }
  if (scene.type === 'confidenceGain') {
    for (const entry of scene.characters ?? []) {
      addCharacterAssets(scenario, entry.characterId, undefined, urls);
    }
  }
  if (scene.type === 'recordCard') {
    addUrl(scene.image, urls);
    addCharacterAssets(scenario, scene.characterId, undefined, urls);
  }

  return [...urls];
}

function collectSceneListAssetUrls(scenes: Scene[], scenario: Scenario): string[] {
  const urls = new Set<string>();
  for (const scene of flattenSceneTree(scenes)) {
    for (const url of collectDirectSceneAssetUrls(scene, scenario)) {
      urls.add(url);
    }
  }
  return [...urls];
}

export function collectMissionAssetUrls(scenario: Scenario, missionId?: string): string[] {
  const structure = buildInlineStructure(scenario);
  if (!missionId) {
    return collectSceneListAssetUrls(scenario.scenes, scenario);
  }
  const group = structure.find((entry) => entry.mission?.id === missionId);
  if (!group) return [];
  const scenes = group.chapters.flatMap((chapter) => chapter.scenes);
  return collectSceneListAssetUrls(scenes, scenario);
}

export function collectChapterAssetUrls(scenario: Scenario, chapterStartSceneId: string): string[] {
  const group = buildChapterGroups(scenario).find(
    (entry) => entry.chapter?.startScene === chapterStartSceneId,
  );
  if (!group) return [];
  return collectSceneListAssetUrls(group.scenes, scenario);
}

export function resolveEntrySceneId(
  scenario: Scenario,
  scenarioId: string,
  missionId?: string,
): string {
  if (typeof window !== 'undefined') {
    const urlSceneId = new URLSearchParams(window.location.search).get('scene');
    if (urlSceneId && getSceneById(scenario, urlSceneId)) {
      return urlSceneId;
    }
  }

  const saved = loadGame(scenarioId, missionId);
  const savedSceneId = saved.data?.gameState.currentSceneId;
  if (saved.success && savedSceneId && getSceneById(scenario, savedSceneId)) {
    return savedSceneId;
  }

  if (missionId) {
    const missionStart = getMissionStartScene(scenario, missionId);
    if (missionStart) return missionStart;
  }

  return scenario.metadata.startScene;
}

export function collectEntrySceneAssetUrls(scenario: Scenario, sceneId: string): string[] {
  const urls = new Set<string>();
  const scene = getSceneById(scenario, sceneId);
  if (scene) {
    for (const url of collectDirectSceneAssetUrls(scene, scenario)) {
      urls.add(url);
    }
  }
  const inheritedBackground = resolveVisualState(scenario, sceneId).background;
  addUrl(inheritedBackground, urls);
  return [...urls];
}

function findChapterGroupForScene(
  scenario: Scenario,
  sceneId: string,
): { group: InlineChapterGroup; index: number; groups: InlineChapterGroup[] } | undefined {
  const groups = buildChapterGroups(scenario);
  for (let index = 0; index < groups.length; index++) {
    const group = groups[index]!;
    const ids = new Set(flattenSceneTree(group.scenes).map((scene) => scene.id));
    if (ids.has(sceneId)) {
      return { group, index, groups };
    }
  }
  return undefined;
}

export function collectRestOfChapterAssetUrls(scenario: Scenario, sceneId: string): string[] {
  const found = findChapterGroupForScene(scenario, sceneId);
  if (!found) {
    return collectLookaheadAssetUrls(scenario, sceneId);
  }
  return collectSceneListAssetUrls(found.group.scenes, scenario);
}

export function collectNextChapterAssetUrls(scenario: Scenario, sceneId: string): string[] {
  const found = findChapterGroupForScene(scenario, sceneId);
  const next = found?.groups[found.index + 1];
  if (!next) return [];
  return collectSceneListAssetUrls(next.scenes, scenario);
}

export function isLastChapterOfMission(scenario: Scenario, sceneId: string): boolean {
  const structure = buildInlineStructure(scenario);
  for (const mission of structure) {
    const chapterIndex = mission.chapters.findIndex((chapter) =>
      flattenSceneTree(chapter.scenes).some((scene) => scene.id === sceneId),
    );
    if (chapterIndex >= 0) {
      return chapterIndex === mission.chapters.length - 1;
    }
  }
  return false;
}

export function collectNextMissionFirstChapterAssetUrls(
  scenario: Scenario,
  sceneId: string,
): string[] {
  const structure = buildInlineStructure(scenario);
  for (let missionIndex = 0; missionIndex < structure.length; missionIndex++) {
    const mission = structure[missionIndex]!;
    const chapterIndex = mission.chapters.findIndex((chapter) =>
      flattenSceneTree(chapter.scenes).some((scene) => scene.id === sceneId),
    );
    if (chapterIndex < 0) continue;
    const nextMission = structure[missionIndex + 1];
    const firstChapter = nextMission?.chapters[0];
    if (!firstChapter) return [];
    return collectSceneListAssetUrls(firstChapter.scenes, scenario);
  }
  return [];
}

export function collectLookaheadSceneIds(
  scenario: Scenario,
  fromSceneId: string,
  limit = LOOKAHEAD_SCENE_COUNT,
): string[] {
  const ordered: string[] = [];
  const seen = new Set<string>([fromSceneId]);

  const push = (id: string | undefined | null): void => {
    if (!id || seen.has(id) || ordered.length >= limit) return;
    seen.add(id);
    ordered.push(id);
  };

  const expandChoice = (sceneId: string) => {
    const scene = getSceneById(scenario, sceneId);
    if (scene?.type !== 'choice') return;
    for (const option of scene.options ?? []) {
      if (option.goto) push(option.goto);
      for (const branchScene of flattenSceneTree(option.scenes ?? [])) {
        push(branchScene.id);
      }
    }
  };

  expandChoice(fromSceneId);

  const walked = new Set<string>();
  let currentId: string | null = resolveNextSceneId(scenario, fromSceneId);
  while (currentId && ordered.length < limit && !walked.has(currentId)) {
    walked.add(currentId);
    push(currentId);
    expandChoice(currentId);
    const nextId = resolveNextSceneId(scenario, currentId);
    if (!nextId || nextId === currentId) break;
    currentId = nextId;
  }

  return ordered;
}

export function collectLookaheadAssetUrls(
  scenario: Scenario,
  fromSceneId: string,
  limit = LOOKAHEAD_SCENE_COUNT,
): string[] {
  const urls = new Set<string>();
  for (const sceneId of collectLookaheadSceneIds(scenario, fromSceneId, limit)) {
    const scene = getSceneById(scenario, sceneId);
    if (scene) {
      for (const url of collectDirectSceneAssetUrls(scene, scenario)) {
        urls.add(url);
      }
    }
    addUrl(resolveVisualState(scenario, sceneId).background, urls);
  }
  return [...urls];
}

export function collectGameplayPrefetchUrls(scenario: Scenario, sceneId: string): string[] {
  const urls = new Set(collectLookaheadAssetUrls(scenario, sceneId));
  const scene = getSceneById(scenario, sceneId);
  if (scene && sceneIsChapterEnd(scene)) {
    for (const url of collectNextChapterAssetUrls(scenario, sceneId)) {
      urls.add(url);
    }
  }
  if (isLastChapterOfMission(scenario, sceneId)) {
    for (const url of collectNextMissionFirstChapterAssetUrls(scenario, sceneId)) {
      urls.add(url);
    }
  }
  return [...urls];
}

function preloadImage(url: string): Promise<void> {
  if (loadedUrls.has(url)) return Promise.resolve();
  const pending = inFlight.get(url);
  if (pending) return pending;

  const promise = new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => {
      loadedUrls.add(url);
      inFlight.delete(url);
      resolve();
    };
    img.onerror = () => {
      inFlight.delete(url);
      resolve();
    };
    img.src = url;
  });
  inFlight.set(url, promise);
  return promise;
}

function kickVideoPrefetch(url: string): void {
  if (loadedUrls.has(url) || inFlight.has(url)) return;
  if (typeof document === 'undefined') return;

  inFlight.set(url, Promise.resolve());
  const video = document.createElement('video');
  video.preload = 'auto';
  video.muted = true;
  video.onloadeddata = () => {
    loadedUrls.add(url);
    inFlight.delete(url);
  };
  video.onerror = () => {
    inFlight.delete(url);
  };
  video.src = url;
}

export interface PreloadAssetOptions {
  waitForVideo?: boolean;
  concurrency?: number;
  onProgress?: (loaded: number, total: number) => void;
}

export async function preloadAssetUrls(
  urls: string[],
  options: PreloadAssetOptions = {},
): Promise<void> {
  const unique = [...new Set(urls.filter(Boolean))];
  const waitForVideo = options.waitForVideo ?? false;
  const concurrency = options.concurrency ?? PRELOAD_CONCURRENCY;

  const jobs: string[] = [];
  for (const url of unique) {
    if (isVideoAsset(url)) {
      if (waitForVideo) {
        jobs.push(url);
      } else {
        kickVideoPrefetch(url);
      }
      continue;
    }
    if (!loadedUrls.has(url)) {
      jobs.push(url);
    }
  }

  const alreadyLoaded = unique.length - jobs.length;
  let completed = alreadyLoaded;
  const total = unique.length;
  options.onProgress?.(total === 0 ? 0 : completed, total);

  if (jobs.length === 0) {
    if (total > 0) options.onProgress?.(total, total);
    return;
  }

  let cursor = 0;
  const worker = async () => {
    while (cursor < jobs.length) {
      const url = jobs[cursor++];
      if (!url) continue;
      try {
        if (isVideoAsset(url)) {
          await preloadVideo(url);
          loadedUrls.add(url);
        } else {
          await preloadImage(url);
        }
      } catch {
        // Keep going — missing assets should not block gameplay.
      }
      completed++;
      options.onProgress?.(completed, total);
    }
  };

  const workers = Array.from(
    { length: Math.min(concurrency, jobs.length) },
    () => worker(),
  );
  await Promise.all(workers);
}

/** Fire-and-forget prefetch: images are cached, videos start without blocking. */
export function prefetchAssetUrls(urls: string[]): void {
  void preloadAssetUrls(urls, { waitForVideo: false });
}
